import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import '../App.css';

interface LocationState {
  formData?: any;
  photos?: { url: string }[];
  reportData?: any;
}

const ConfirmationPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showCheckmark, setShowCheckmark] = useState(false);

  // Check if we have the required state
  const state = location.state as LocationState;
  const photos = useMemo(() => state?.photos || [], [state?.photos]);

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
    
    // If no state is present, redirect to home
    if (!state?.formData || !photos || photos.length === 0) {
      navigate('/');
      return;
    }
    // Trigger checkmark animation after component mounts
    setShowCheckmark(true);

    // Clean up object URLs
    return () => {
      photos.forEach(photo => {
        if (photo.url && photo.url.startsWith('blob:')) {
          URL.revokeObjectURL(photo.url);
        }
      });
    };
  }, [navigate, state?.formData, photos]);

  if (!state?.formData || !photos || photos.length === 0) {
    return null;
  }

  const handleSeeAllViolations = () => {
    // Navigate to the community page to see all violations
    navigate('/community');
  };

  const handleSubmitAnother = () => {
    // Navigate back to home to submit another report
    navigate('/');
  };

  return (
    <div className="main-content">
      <h1 className="main-title">Report Submitted!</h1>
      
      <div className={`confirmation-checkmark-container ${showCheckmark ? 'show' : ''}`}>
        <svg 
          className="confirmation-checkmark" 
          xmlns="http://www.w3.org/2000/svg" 
          viewBox="0 0 52 52"
        >
          <circle className="confirmation-checkmark-circle" cx="26" cy="26" r="25" fill="none"/>
          <path className="confirmation-checkmark-check" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8"/>
        </svg>
      </div>

      <div className="confirmation-sections">
        <div className="confirmation-section">
          <h3 className="section-title">What Happens Next?</h3>
          <p className="section-text">
            We keep track of and aggregate all offenses associated with your reported vehicle and mail the cases directly to the city hall. We also report hot zones that are known to have many parking violations, relaying the community feedback directly to the city government to drive changes.
          </p>
        </div>

        <div className="confirmation-section">
          <h3 className="section-title">What Else Can I Do?</h3>
          <p className="section-text">
            Invite your friends to report alongside you. The more reports we have to file alongside, the more pressure we can bring about from the local officials.
          </p>
        </div>

        <div className="confirmation-section">
          <h3 className="section-title">Making a Difference</h3>
          <p className="section-text">
            Every report helps create safer streets for everyone. Your contribution joins thousands of others working together to improve our community's parking compliance and pedestrian safety.
          </p>
        </div>
      </div>

      <div className="confirmation-actions">
        <button 
          className="action-btn upload-btn"
          onClick={handleSeeAllViolations}
        >
          See All Violations
        </button>

        <button 
          className="action-btn secondary-btn"
          onClick={handleSubmitAnother}
        >
          New Report
        </button>
      </div>

      <div className="confirmation-privacy-section">
        <p className="privacy-text">
          <strong>Privacy Protected:</strong> Your report has been submitted anonymously. You will not be contacted by the city or the violator. We never share personal data with anyone.
        </p>
      </div>

    </div>
  );
};

export default ConfirmationPage;
